import * as React from 'react';

import cx from 'classnames';

import { Box } from '~shared/components/Box';
import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';

const variantClassMap = {
  default: {
    disabled: 'text-grey-400 bg-transparent',
  },
  primary: {
    disabled: 'text-white bg-grey-400',
  },
  secondary: {
    disabled: 'text-grey-600 bg-grey-50',
  },
  ghost: {
    disabled: 'text-grey-400 bg-transparent',
  },
};

type IconButtonElement = HTMLAnchorElement | HTMLButtonElement;
export interface IconButtonProps
  extends React.AllHTMLAttributes<IconButtonElement> {
  children: React.ReactNode;
  /** The style variant of the button. */
  appearance?: 'default' | 'primary' | 'secondary' | 'ghost';
  /** Modifies the focus/hover bg colour. Not required for `primary` appearance. */
  withAltBackground?: boolean;
  /** Changes the hover styles to indicate a potentially destructive action. */
  isRisky?: boolean;
  DEBUG_HOVER?: boolean;
}

export const IconButton = React.forwardRef<IconButtonElement, IconButtonProps>(
  function IconButton(
    {
      children,
      href,
      onClick,
      appearance = 'default',
      withAltBackground,
      isRisky,
      DEBUG_HOVER,
      ...props
    }: IconButtonProps,
    forwardedRef
  ) {
    const isDisabled = props.disabled;
    const buttonProps = {
      href,
      onClick,
      type: onClick ? 'button' : undefined,
    };

    const buttonClass = cx(
      'group relative focus:z-10 flex items-center justify-center w-12 h-12 p-0 m-0',
      'border-0 rounded-2 appearance-none outline-none',
      'transition ease-in-out duration-150',
      isDisabled
        ? variantClassMap[appearance].disabled
        : [
            'cursor-pointer',
            {
              'text-cyan-700':
                ['default', 'secondary'].includes(appearance) && !DEBUG_HOVER,
              'hover:text-cyan-800 focus:text-cyan-700':
                appearance === 'default' && !isRisky,
              'hover:bg-cyan-50 focus:bg-cyan-50':
                appearance === 'default' && !withAltBackground && !isRisky,
              'text-cyan-800':
                appearance === 'default' && DEBUG_HOVER && !isRisky,
              'bg-cyan-50':
                appearance === 'default' &&
                DEBUG_HOVER &&
                !withAltBackground &&
                !isRisky,
              'bg-transparent': ['default', 'ghost'].includes(appearance),
              'hover:bg-white focus:bg-white':
                appearance === 'default' && withAltBackground,
              'bg-white':
                appearance === 'default' && withAltBackground && DEBUG_HOVER,

              'text-white': appearance === 'primary',
              'bg-cyan-500 hover:bg-cyan-600 focus:bg-cyan-600':
                appearance === 'primary',
              'bg-cyan-600': appearance === 'primary' && DEBUG_HOVER,

              'bg-cyan-50 hover:bg-cyan-200 focus:bg-cyan-200':
                appearance === 'secondary',
              'bg-cyan-200': appearance === 'secondary' && DEBUG_HOVER,

              'text-grey-600 hover:text-cyan-600 focus:text-cyan-600':
                appearance === 'ghost' && withAltBackground,
              'text-grey-800 hover:bg-grey-50 focus:bg-grey-50':
                appearance === 'ghost' && !withAltBackground,
              'text-cyan-600 ': appearance === 'ghost' && DEBUG_HOVER,
              'bg-grey-50':
                appearance === 'ghost' && DEBUG_HOVER && !withAltBackground,

              'hover:text-red-700 focus:text-red-700': isRisky,
              'hover:bg-red-50 focus:bg-red-50': isRisky && !withAltBackground,
              'text-red-700': isRisky && DEBUG_HOVER,
              'bg-red-50': isRisky && !withAltBackground && DEBUG_HOVER,
            },
          ]
    );

    const focusClass = cx({
      'text-red-700': isRisky,
    });

    return (
      <Box
        tagName={onClick ? 'button' : 'a'}
        ref={forwardedRef}
        className={buttonClass}
        {...buttonProps}
        {...props}
      >
        <FocusIndicator
          colorClass={focusClass}
          notOffset={appearance === 'ghost' && withAltBackground}
        />
        {children}
      </Box>
    );
  }
);
